import { AxiosError, isAxiosError as isAxiosErrorAxios } from 'axios';
import * as Sentry from '@sentry/react';
import i18n from '@portal/i18n/config';
import storeCreator from '@portal/reducers/createStore';
import { AuthActions } from '@portal/actions';
import ErrorsMessages from '@portal/enum/errors';
import { translate } from '@portal/services/i18n';
import AlertService from '@portal/services/AlertService';
import { getDeviceId } from '@portal/services/storage';
import { WL_BANK_CODE } from '@portal/config/env';

interface ApiErrorResponse {
  error?: string | { [key: string]: unknown };
  message?: string;
  code?: string;
  errorCode?: string;
  options?: {
    message?: string;
  };
}

const isAxiosError = (
  error: unknown
): error is AxiosError<ApiErrorResponse> => {
  return isAxiosErrorAxios(error);
};

export { isAxiosError };

const MAX_PAYLOAD = 8000;

function safeStringify(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  try {
    const str = typeof value === 'string' ? value : JSON.stringify(value);
    return str.length > MAX_PAYLOAD
      ? str.slice(0, MAX_PAYLOAD) + '…[truncated]'
      : str;
  } catch {
    return String(value).slice(0, 500);
  }
}

function safeForSentry(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  )
    return value;
  try {
    const str = JSON.stringify(value);
    return str.length > MAX_PAYLOAD
      ? str.slice(0, MAX_PAYLOAD) + '…[truncated]'
      : JSON.parse(str);
  } catch {
    return String(value).slice(0, 500);
  }
}

const SENSITIVE_HEADERS = [
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
];

function safeHeaders(
  headers?: Record<string, unknown>
): Record<string, string> | undefined {
  if (!headers) return undefined;
  const safe: Record<string, string> = {};
  for (const [key, val] of Object.entries(headers)) {
    if (SENSITIVE_HEADERS.includes(key.toLowerCase())) {
      safe[key] = '[REDACTED]';
    } else {
      safe[key] = String(val);
    }
  }
  return safe;
}

const EXPECTED_ERRORS = new Set(
  [
    ErrorsMessages.INVALID_CREDENTIALS,
    ErrorsMessages.BLOCKED_ACCOUNT,
    ErrorsMessages.BLOCKED_ACCOUNT_ERROR,
    ErrorsMessages.INVALID_GRANT,
    ErrorsMessages.MAX_DEVICES_PER_USER,
    ErrorsMessages.TRANSACTION_NOT_APPROVED,
    ErrorsMessages.TRANSACTION_LAST_ATTEMPT_BEFORE_BLOCK,
    ErrorsMessages.TRANSACTION_FINAL_ATTEMPT,
    ErrorsMessages.UNAVAILABLE_ACCOUNT,
    ErrorsMessages.INSUFFICIENT_FUNDS,
    ErrorsMessages.NEXT_TO_LAST_ATTEMPT,
    ErrorsMessages.LAST_ATTEMPT,
    ErrorsMessages.PIX_INVALID_PARAMS,
    ErrorsMessages.PIX_KEY_NOT_FOUND,
    ErrorsMessages.INVALID_TOKEN,
    ErrorsMessages.DISABLED_USER,
    ErrorsMessages.REJECTED_ACCOUNT,
    ErrorsMessages.UNAUTHORIZED_DEVICE,
    ErrorsMessages.CLAIM_ALREADY_EXISTS_FOR_KEY,
    ErrorsMessages.USER_OR_ACCOUNT_FRAUD_RESTRICTION,
    ErrorsMessages.EXPIRED_SESSION,
    ErrorsMessages.PIX_KEY_FRAUD,
    ErrorsMessages.INVALID_CODE_AREA,
    ErrorsMessages.LIMIT_NUMBER_OF_KEYS,
    ErrorsMessages.INVALID_PIX_KEY,
    ErrorsMessages.PORTABILITY_ERROR,
    ErrorsMessages.PORTABILITY_ERROR_CONFIRM,
    ErrorsMessages.CARD_CREATION_IN_PROCESS,
    ErrorsMessages.ENTITY_NOT_FOUND,
    ErrorsMessages.YOU_CANNOT_PERFORM_THIS,
    ErrorsMessages.ADDRESS_NOT_FOUND,
    ErrorsMessages.ADDRESS_NOT_FOUND_RETRY_MANUAL,
    ErrorsMessages.PAYMENT_OUTSIDE_OF_HOURS_ALLOWED,
    ErrorsMessages.BLOCKED_DEVICE,
    ErrorsMessages.SERVICE_UNAVAILABLE,
    ErrorsMessages.INTERNAL_ERROR,
    ErrorsMessages.NETWORK_ERROR,
    ErrorsMessages.FORBIDDEN_ACCESS,
    ErrorsMessages.TIMEOUT_OF_10000MS_EXCEEDED,
  ].map((v) => v.toLowerCase())
);

function normalizeErrorStringForMatch(str: string): string {
  const withUnderscores = str.replace(/\s+/g, '_').trim();
  const fromPascalOrCamel = withUnderscores.replace(
    /[A-Z]/g,
    (c) => `_${c.toLowerCase()}`
  );
  const normalized = (
    fromPascalOrCamel.startsWith('_')
      ? fromPascalOrCamel.slice(1)
      : fromPascalOrCamel
  ).toLowerCase();
  return normalized;
}

function isExpectedErrorString(errorString: string | undefined): boolean {
  if (!errorString || typeof errorString !== 'string') return false;
  const normalized = normalizeErrorStringForMatch(errorString);
  if (EXPECTED_ERRORS.has(normalized)) return true;
  return EXPECTED_ERRORS.has(errorString.toLowerCase());
}

export function isExpectedErrorKey(key: string): boolean {
  const normalized = normalizeErrorStringForMatch(key);
  return EXPECTED_ERRORS.has(normalized);
}

const handledUiSet = new WeakSet<AxiosError>();

export class ApiErrorHandler {
  private static getErrorString(error?: unknown): string | undefined {
    if (typeof error === 'string') {
      return error;
    }
    if (typeof error === 'object' && error !== null) {
      const obj = error as Record<string, unknown>;
      const keys = Object.keys(obj);
      if (keys.length > 0) {
        const firstValue = obj[keys[0]];
        return typeof firstValue === 'string' ? firstValue : keys[0];
      }
    }
    return undefined;
  }

  private static getErrorStringFromResponse(data: unknown): string | undefined {
    if (typeof data !== 'object' || data === null) return undefined;
    const d = data as ApiErrorResponse;
    return (
      (typeof d.errorCode === 'string' ? d.errorCode : undefined) ??
      ApiErrorHandler.getErrorString(d.error) ??
      (typeof d.code === 'string' ? d.code : undefined) ??
      (typeof d.message === 'string' ? d.message : undefined)
    );
  }

  private static isInfraResponse(response: AxiosError['response']): boolean {
    if (!response) return false;
    const headers = response.headers as Record<string, unknown> | undefined;
    const contentType = String(headers?.['content-type'] ?? '');
    const serverHeader = String(headers?.['server'] ?? '');
    return (
      contentType.toLowerCase().includes('text/html') ||
      /awselb|cloudfront|nginx|cloudflare/i.test(serverHeader)
    );
  }

  private static handleBlockedAccount(): void {
    if (window.location.pathname === '/') {
      return;
    }

    setTimeout(() => {
      const alertService = AlertService.getInstance();
      alertService.custom({
        title: translate('APPLICATION.ERRORS.BLOCKED_MODAL.TITLE'),
        message: translate('APPLICATION.ERRORS.BLOCKED_MODAL.MESSAGE'),
        cancelable: false,
        closeButton: false,
        buttons: [
          {
            text: translate('APPLICATION.ERRORS.BLOCKED_MODAL.BUTTON'),
            onPress: () => {
              storeCreator.dispatch(AuthActions.logout());
            },
          },
        ],
      });
    }, 0);
  }

  static async handle(error?: unknown): Promise<void> {
    if (!isAxiosError(error)) {
      if (error != null) {
        Sentry.captureException(
          error instanceof Error ? error : new Error(String(error)),
          {
            tags: { 'error.domain': 'api' },
            extra: { source: 'request_interceptor_or_other' },
          }
        );
      }
      return;
    }

    const axiosError = error as AxiosError<ApiErrorResponse>;
    const config = axiosError.config;
    const response = axiosError.response;
    const status = response?.status ?? null;
    const method = (config?.method ?? 'UNKNOWN').toUpperCase();
    const url = config?.url ?? 'unknown';
    const fullUrl = config?.baseURL ? `${config.baseURL}${url}` : url;
    const displayUrl =
      (typeof fullUrl === 'string' && fullUrl.trim()) ||
      (typeof url === 'string' && url.trim()) ||
      '[unknown-url]';
    const errorResponse = response?.data;
    const errorString =
      ApiErrorHandler.getErrorStringFromResponse(errorResponse);
    const normalizedErrorString = errorString?.toLowerCase() ?? '';
    const isInfra = ApiErrorHandler.isInfraResponse(response);

    const responseHeaders = response?.headers as
      | Record<string, unknown>
      | undefined;
    const requestId =
      (typeof responseHeaders?.['x-correlation-id'] === 'string'
        ? (responseHeaders['x-correlation-id'] as string)
        : undefined) ??
      (typeof responseHeaders?.['x-request-id'] === 'string'
        ? (responseHeaders['x-request-id'] as string)
        : undefined);

    const authStateSlice = (
      storeCreator.getState() as { auth?: { accessToken?: string | null } }
    )?.auth;
    const authState: 'logged_in' | 'anonymous' = authStateSlice?.accessToken
      ? 'logged_in'
      : 'anonymous';

    const clientSlug = WL_BANK_CODE ?? 'default';
    const locale = i18n.language ?? 'pt-BR';
    const deviceId = (() => {
      try {
        return getDeviceId();
      } catch {
        return undefined;
      }
    })();

    const tags = {
      'error.domain': 'api',
      'http.status_code': String(status ?? 'no_response'),
      'http.method': method,
      'http.url': displayUrl,
      'auth.state': authState,
      'client.slug': clientSlug,
      'i18n.locale': locale,
      ...(requestId ? { 'request.id': requestId } : {}),
      ...(deviceId ? { 'session.device_id': String(deviceId) } : {}),
    };
    const extra = {
      full_url: fullUrl,
      display_url: displayUrl,
      request_id: requestId,
      auth_state: authState,
      client_slug: clientSlug,
      i18n_locale: locale,
      request_method: method,
      request_headers: safeHeaders(config?.headers as Record<string, unknown>),
      request_params: safeForSentry(config?.params),
      request_payload: safeStringify(config?.data),
      request_timeout: config?.timeout,
      response_status: status,
      response_status_text: response?.statusText,
      response_headers: safeHeaders(
        response?.headers as Record<string, unknown>
      ),
      response_body: safeStringify(response?.data),
    };
    const contexts = {
      'API Error Request': {
        method,
        request_url: displayUrl,
        full_url: fullUrl,
        headers: safeHeaders(config?.headers as Record<string, unknown>),
        query_string: safeStringify(config?.params),
        request_payload: safeStringify(config?.data),
        timeout: config?.timeout,
      },
      'API Error Response': {
        status_code: status,
        status_text: response?.statusText,
        headers: safeHeaders(response?.headers as Record<string, unknown>),
        response_body: safeStringify(response?.data),
      },
    };

    if (isInfra && status !== null) {
      Sentry.captureEvent({
        message: `[infra] ${method} ${displayUrl} -> ${status}`,
        level: 'warning',
        tags: { ...tags, 'error.source': 'infrastructure' },
        extra,
        contexts,
        fingerprint: [
          'api-infra',
          clientSlug,
          method,
          displayUrl,
          String(status),
        ],
        transaction: `api.infra.${method}`,
      });
      return;
    }

    const isExpectedError =
      status !== null &&
      status >= 400 &&
      status < 500 &&
      errorString !== undefined &&
      isExpectedErrorString(errorString);

    if (isExpectedError) {
      Sentry.captureEvent({
        message: `[expected] ${method} ${displayUrl} -> ${status}: ${errorString}`,
        level: 'warning',
        tags,
        extra,
        contexts,
        fingerprint: [
          'api-expected',
          clientSlug,
          method,
          displayUrl,
          normalizedErrorString,
        ],
        transaction: `api.expected.${normalizedErrorString}`,
      });
      ApiErrorHandler.runUiHandling(axiosError, normalizedErrorString);
      return;
    }

    const axiosMessage = String(axiosError.message ?? '');
    const isNetworkOrTimeout =
      status === null &&
      (/Network Error|timeout of \d+ms exceeded|ECONNABORTED|ECONNRESET|ETIMEDOUT/i.test(
        axiosMessage
      ) ||
        axiosError.code === 'ERR_NETWORK' ||
        axiosError.code === 'ECONNABORTED');

    if (isNetworkOrTimeout) {
      const kind = axiosMessage.toLowerCase().includes('timeout')
        ? 'timeout'
        : 'network';
      Sentry.captureEvent({
        message: `[network] ${method} ${displayUrl}: ${axiosMessage}`,
        level: 'warning',
        tags: { ...tags, 'error.type': `network.${kind}` },
        extra,
        contexts,
        fingerprint: ['api-network', clientSlug, method, displayUrl, kind],
        transaction: `api.network.${kind}`,
      });
      ApiErrorHandler.runUiHandling(axiosError, normalizedErrorString);
      return;
    }

    const sentryError = new Error(
      `${method} ${displayUrl} -> ${status ?? 'no response'}: ${
        axiosError.message
      }`
    );
    if (!sentryError.name || sentryError.name === 'Error') {
      sentryError.name = `ApiError[${status ?? 'no_response'}]`;
    }
    Sentry.captureException(sentryError, {
      tags,
      extra,
      contexts,
      fingerprint: [
        'api-error',
        clientSlug,
        method,
        displayUrl,
        String(status ?? 'no_response'),
      ],
    });
    ApiErrorHandler.runUiHandling(axiosError, normalizedErrorString);
  }

  private static runUiHandling(
    axiosError: AxiosError,
    normalizedErrorString: string
  ): void {
    if (!normalizedErrorString) return;
    if (handledUiSet.has(axiosError)) return;

    switch (normalizedErrorString) {
      case ErrorsMessages.BLOCKED_ACCOUNT_ERROR.toLowerCase():
      case ErrorsMessages.BLOCKED_ACCOUNT.toLowerCase():
        handledUiSet.add(axiosError);
        ApiErrorHandler.handleBlockedAccount();
        break;
      default:
        break;
    }
  }
}

export default ApiErrorHandler;
